/* eslint-disable @typescript-eslint/no-explicit-any */
import { jwtDecode } from 'jwt-decode';
import { AuthUser, JWTTokenDTO } from '../redux/identity/identityTypes';
import {
 deleteCookieCustom,
 getCookieCustom,
 setCookieCustom,
} from './cookies';
import {
 removeConfigStorageItem,
 setConfigStorageItem,
} from '../hooks/useConfigStorage';

export const AUTH_TOKENS_PERSISTED_EVENT = 'scales-auth-tokens-persisted';

const LAST_CONNECTION_KEY_STORAGE_KEY = 'lastConnectionKey';

export const saveLastConnectionKey = (connectionKey: number | string) => {
 if (typeof window === 'undefined') {
  return;
 }
 const parsed = Number(connectionKey);
 if (!Number.isFinite(parsed) || parsed <= 0) {
  return;
 }
 localStorage.setItem(LAST_CONNECTION_KEY_STORAGE_KEY, String(parsed));
};

export const getLastConnectionKey = (): number | null => {
 if (typeof window === 'undefined') {
  return null;
 }
 const raw = localStorage.getItem(LAST_CONNECTION_KEY_STORAGE_KEY);
 if (!raw) {
  return null;
 }
 const parsed = Number(raw);
 return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

export const getActiveConnectionKey = (): number | null => {
 const rawUser = getCookieCustom('user');
 if (rawUser) {
  try {
   const parsedUser = JSON.parse(rawUser) as { currentlyActiveDb?: string | number };
   const activeDb = Number(parsedUser?.currentlyActiveDb);
   if (Number.isFinite(activeDb) && activeDb > 0) {
    return activeDb;
   }
  } catch {
   return getLastConnectionKey();
  }
 }
 return getLastConnectionKey();
};

export const clearLastConnectionKey = () => {
 if (typeof window === 'undefined') {
  return;
 }
 localStorage.removeItem(LAST_CONNECTION_KEY_STORAGE_KEY);
};

export const getLoginUserFromAccessToken = (
 accessToken: string | null | undefined,
): AuthUser | null => {
 if (!accessToken) {
  return null;
 }
 try {
  return jwtDecode(accessToken) as AuthUser;
 } catch {
  return null;
 }
};

export const getLoginUserFromCookie = (): AuthUser | null =>
 getLoginUserFromAccessToken(getCookieCustom('access_token'));

export const getUserIdFromAccessToken = (
 accessToken: string | null | undefined,
): string | null => getLoginUserFromAccessToken(accessToken)?.userId ?? null;

const clearLastConnectionKeyIfUserChanged = (
 nextAccessToken: string | null | undefined,
) => {
 if (!nextAccessToken) {
  return;
 }
 const previousUserId = getUserIdFromAccessToken(
  getCookieCustom('access_token'),
 );
 if (!previousUserId) {
  return;
 }
 const nextUserId = getUserIdFromAccessToken(nextAccessToken);
 if (nextUserId && previousUserId !== nextUserId) {
  clearLastConnectionKey();
 }
};

const DEFAULT_ACCESS_EXPIRATION_MINUTES = 100;
const REFRESH_EXPIRATION_MINUTES = 60 * 24 * 30;

const getAccessExpirationMinutes = (tokenData: JWTTokenDTO) => {
 const accessExpiration = Number(tokenData.expiration);
 if (!Number.isFinite(accessExpiration) || accessExpiration <= 0) {
  return DEFAULT_ACCESS_EXPIRATION_MINUTES;
 }
 return accessExpiration;
};

export const persistAuthTokens = (tokenData: JWTTokenDTO) => {
 if (!tokenData.access_token) {
  return;
 }

 clearLastConnectionKeyIfUserChanged(tokenData.access_token);

 const accessExpiration = getAccessExpirationMinutes(tokenData);
 const decoded = jwtDecode(tokenData.access_token);

 setCookieCustom('user', JSON.stringify(decoded), accessExpiration);
 setCookieCustom('access_token', tokenData.access_token, accessExpiration);
 setCookieCustom('expiration', accessExpiration.toString(), accessExpiration);
 setCookieCustom('user_id', (decoded as any)?.userId || '', accessExpiration);

 if (tokenData.refresh_token) {
  setCookieCustom(
   'refresh_token',
   tokenData.refresh_token,
   REFRESH_EXPIRATION_MINUTES,
  );
 }

 if (tokenData.configuration) {
  setConfigStorageItem({
   key: 'configuration',
   value: JSON.stringify(tokenData.configuration),
  });
 }

 if (typeof window !== 'undefined') {
  window.dispatchEvent(new Event(AUTH_TOKENS_PERSISTED_EVENT));
 }
};

export const clearAuthTokens = () => {
 deleteCookieCustom('access_token', '/');
 deleteCookieCustom('refresh_token', '/');
 deleteCookieCustom('expiration', '/');
 deleteCookieCustom('user_id', '/');
 removeConfigStorageItem({ key: 'configuration' });
};

export const getAuthCookies = () => {
 return {
  accessToken: getCookieCustom('access_token'),
  refreshToken: getCookieCustom('refresh_token'),
 };
};

